import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, AfterViewInit } from '@angular/core';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonBindingDataService } from '../../../services/common-binding-data.service';

import { TranslateModule } from '@ngx-translate/core';
import { AppIcons } from 'app/modules/shared/app.icons';
import { MiImageContainerComponent } from 'app/modules/shared/ui-sharable/mi-image-container/mi-image-container.component';
import { MessageService } from 'primeng/api';
import { FileUploadModule } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { AppSettings } from '../../../app.settings';
import { ConfigService } from '../../../services/config.service';
import { EntityService } from '../../../services/entity.service';
import { BlockUIModule } from 'primeng/blockui';
import { JsonPipe } from '@angular/common';


interface UploadedFiles {
  file: File,
  fileId: string,
  isExisting?: boolean,
  url?: string
}

@Component({
  selector: 'app-mi-upload-file',
  templateUrl: './mi-upload-file.component.html',
  styleUrls: ['./mi-upload-file.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, FileUploadModule, ToastModule, TranslateModule, MiImageContainerComponent,
    BlockUIModule, JsonPipe]
})
export class MiUploadFileComponent implements OnInit, AfterViewInit {
  @Input() field: any;
  formName: FormGroup;
  @Input() attributeLabels: [];
  @Input() moduleName: string;
  @Input() docType: string;
  @Input() showImg: boolean = false;
  @Input() isMultipleFilesUpload: boolean = false;
  @Output() fileUploadEvent: EventEmitter<any> = new EventEmitter<any>();
  miIcons = AppIcons;
  chooseLabel: string = this.cs.getLabel('lbl_select_document');
  accept: string;
  fileUploadInstruction: string;
  chooseIcon: any = null;
  dropAreaMsg: any = '';
  dropAreaMsgForDocument: string = this.cs.getLabel('lbl_drop_document');
  maxFileSize: any = AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE;
  entityType: any = AppSettings.ENTITY_TYPE;
  img: any;
  showCheckIcon: boolean = true;
  isOneToManyRelation: boolean = false;
  docUrl: any = [];
  showDoc: boolean = false;
  blockedDocument: boolean = false;
  uploadedImg: any[] = [];
  uploadedFiles: UploadedFiles[] = [];
  uploadedFileIds: string[] = [];
  isFormReady: boolean = false;
  documentLimitMessage;

  constructor(private formGroupDirective: FormGroupDirective,
    private messageService: MessageService,
    private cd: ChangeDetectorRef,
    public cs: CommonBindingDataService,
    private configService: ConfigService,
    private entityService: EntityService) {
    this.formName = this.formGroupDirective.control;
  }

  ngOnInit() {
    if (this.moduleName === 'protocol' || this.moduleName === 'order') {
      this.documentLimitMessage = this.cs.getLabel('lbl_document_multiple');
    } else if (this.moduleName === 'protocol_product' || this.moduleName === 'protocol_purchase_order' || this.moduleName === 'protocol_site') {
      this.documentLimitMessage = this.cs.getLabel('lbl_document_single');
    }
    if (this.moduleName === AppSettings.ENTITY_CODE.DRIVER_DOCUMENT || this.moduleName === AppSettings.ENTITY_CODE.VEHICLE_DOCUMENT) {
      this.isOneToManyRelation = true;
      this.setImageOrDocumentForOneToManyRelation();
    } else {
      this.setImageForOneToOneRelation();
    }

    if (this.field?.attributeCode === AppSettings.ATTRIBUTE_CODE.UPLOAD_DOCUMENT) {
      if (this.field?.attributeValue && Array.isArray(this.field.attributeValue)) {
        for (const ids of this.field.attributeValue) {
          this.getDocumentByFileId(ids);
        }
      }
    }

    if (this.field.inputCode === 'fileImgPdf' || this.field.inputCode === 'entitySection') {
      this.accept = `${AppSettings.IMAGE_UPLOAD_TYPES},${AppSettings.PDF_UPLOAD}`;
      this.fileUploadInstruction = this.cs.getLabel('lbl_img_pdf_upload_instruction');
      this.chooseLabel = this.cs.getLabel('lbl_select_document');
      this.dropAreaMsg = this.cs.getLabel('lbl_drop_document');
    } else if (this.field.inputCode === 'fileImg') {
      this.accept = AppSettings.IMAGE_UPLOAD_TYPES;
      this.fileUploadInstruction = this.cs.getLabel('lbl_img_upload_instruction');
      this.chooseLabel = this.cs.getLabel('lbl_select_image');
      this.dropAreaMsg = this.cs.getLabel('lbl_drop_img');
    }
    this.setLabelsAccordindgToAttributeCode();
    this.loadExistingFiles();
    this.updateFormControl();
    this.isFormReady = true;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateFormControl();
    }, 100);
  }

  updateFormControl() {
    if (this.uploadedFileIds.length > 0) {
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode]?.setValue(this.uploadedFileIds);
      this.formName.get(this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode)?.updateValueAndValidity();
      this.fileUploadEvent.emit(this.uploadedFileIds);
    }
  }

  private toFileIdArray(value: any): string[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [String(value)];
  }

  loadExistingFiles() {
    if (this.field?.documentURLs && Array.isArray(this.field.documentURLs) && this.field.documentURLs.length > 0) {
      const rawIds = this.formName.controls[this.field.attributeCode]?.value;
      const existingFileIds = this.toFileIdArray(rawIds);
      if (existingFileIds.length > 0) {
        existingFileIds.forEach((fileId: string, index: number) => {
          const url = this.field.documentURLs[index];
          const name = this.field.documentNames?.[index] || `document_${index + 1}`;
          if (url) {
            const existingFile: UploadedFiles = {
              file: new File([], name),
              fileId,
              isExisting: true,
              url
            };
            this.uploadedFiles.push(existingFile);
            this.uploadedFileIds.push(fileId);
            this.uploadedImg.push(url);
          }
        });
      }
    } else if (this.field?.attributeValue && Array.isArray(this.field.attributeValue) && this.field.attributeValue.length > 0) {
      this.field.attributeValue.forEach((fileId: string, index: number) => {
        const url = this.field.documentURLs ? this.field.documentURLs[index] : null;
        const existingFile: UploadedFiles = {
          file: new File([], `document_${index + 1}`),
          fileId,
          isExisting: true,
          url
        };
        this.uploadedFiles.push(existingFile);
        this.uploadedFileIds.push(fileId);
        if (url) {
          this.uploadedImg.push(url);
        }
      });
    }
  }

  setImageForOneToOneRelation() {
    const fileIds = [];
    if (this.field?.imgId) {
      fileIds.push(this.field?.imgId);
      this.img = this.field?.imgUrl ? this.field?.imgUrl : null;
    } else {
      const imgIdAndUrl = this.formName.controls[this.field.attributeCode]?.value;
      if (imgIdAndUrl && imgIdAndUrl?.imgId) {
        this.uploadedFileIds.push(imgIdAndUrl.imgId);
        this.img = (imgIdAndUrl?.imgUrl && imgIdAndUrl?.imgUrl.length > 0) ? imgIdAndUrl?.imgUrl[0] : null;
        if (imgIdAndUrl?.imgUrl.length > 0 && this.isMultipleFilesUpload) {
          this.uploadedImg = imgIdAndUrl.imgUrl;
        }
        this.formName.controls[this.field.attributeCode].setValue(imgIdAndUrl.imgId);
      }
    }
  }

  setImageOrDocumentForOneToManyRelation() {
    const imgIdAndUrl = this.formName.controls[`${this.field.attributeCodeOneToMultiple}_url`]?.value;
    if (imgIdAndUrl && imgIdAndUrl?.imgId) {
      this.uploadedFileIds.push(imgIdAndUrl.imgId);
      this.docUrl.push(imgIdAndUrl?.imgUrl);
    }
  }

  setLabelsAccordindgToAttributeCode() {
    if (this.field.attributeCode === 'event_image') {
      this.chooseLabel = this.cs.getLabel('lbl_select_logo');
      this.dropAreaMsg = this.cs.getLabel('lbl_drop_logo');
    }
  }

  openFile(formControl) {
    const documentURL = formControl.split('_');
    if (documentURL?.length && documentURL[0]) {
      const win = window.open();
      if (win) {
        const iframe = win.document.createElement('iframe');
        iframe.src = this.docUrl[documentURL[0]];
        iframe.style.cssText = 'border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;';
        iframe.setAttribute('allowfullscreen', '');
        win.document.body.appendChild(iframe);
      }
    }
  }

  previewImage(event, field) {
    const imgItem = field.split("_");
    if ((this.docUrl.length - 1) < imgItem[0]) {
      this.docUrl.push(null);
    }
    const file = event.files[0];
    if (file.type === AppSettings.PDF_UPLOAD) {
      const reader = new FileReader();
      reader.onloadend = () => {
        this.docUrl[imgItem[0]] = reader.result as string;
      };
      reader.readAsDataURL(file);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      this.img = reader.result;
      if (!this.isMultipleFilesUpload) {
        this.uploadedImg = [];
      }
      this.uploadedImg.push(this.img);
      if (this.moduleName === AppSettings.ENTITY_CODE.VEHICLE_DOCUMENT || this.moduleName === AppSettings.ENTITY_CODE.DRIVER_DOCUMENT) {
        this.docUrl[imgItem[0]] = reader.result;
      }
    }
    reader.readAsDataURL(event.files[0]);
  }

  showCloseImageIcon() {
    this.showCheckIcon = false;
  }

  showCheckImageIcon() {
    this.showCheckIcon = true;
  }

  removeImage(file, field) {
    const imageIndex = field.split('_');
    this.docUrl.splice(imageIndex[0], 1);
    this.showImg = false;
    const index = this.uploadedFiles.findIndex(ele => ele.fileId === file.fileId);
    if (index !== -1) {
      this.uploadedFiles.splice(index, 1);
    }
    const fileIdIndex = this.uploadedFileIds.findIndex(ele => ele === file.fileId);
    if (fileIdIndex !== -1) {
      this.uploadedFileIds.splice(fileIdIndex, 1);
    }
    if (file.objectURL && file.objectURL?.changingThisBreaksApplicationSecurity) {
      const imgIndex = this.uploadedImg.findIndex(ele => ele === file.objectURL.changingThisBreaksApplicationSecurity);
      if (imgIndex !== -1) {
        this.uploadedImg.splice(imgIndex, 1);
      }
    }
    if (file.url) {
      const imgIndex = this.uploadedImg.findIndex(ele => ele === file.url);
      if (imgIndex !== -1) {
        this.uploadedImg.splice(imgIndex, 1);
      }
    }
    this.img = null;
    this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode]?.setValue(this.uploadedFileIds);
    this.formName.get(this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode)?.updateValueAndValidity();
    this.fileUploadEvent.emit(this.uploadedFileIds);
    this.cd.detectChanges();
  }

  onUpload(event, field) {
    if (this.isMultipleFilesUpload && this.uploadedFiles.length >= 5) {
      this.messageService.add({ key: "fileupload", severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_max_files_limit_reached') || 'Maximum 5 files allowed' });
      return;
    }

    if (this.checkFileExtension(event.files[0])) {
      this.previewImage(event, field);

      const maxFilesToProcess = this.isMultipleFilesUpload ? Math.min(event.files.length, 5 - this.uploadedFiles.length) : event.files.length;

      if (this.isMultipleFilesUpload && event.files.length > maxFilesToProcess) {
        this.messageService.add({ key: "fileupload", severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_max_files_limit_reached') || 'Maximum 5 files allowed' });
      }

      for (let i = 0; i < maxFilesToProcess; i++) {
        const file = event.files[i];
        if (file) {
          if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
            this.messageService.add({ key: "fileupload", severity: 'error', summary: 'Error', detail: this.cs.getLabel('err_file_size_exceed_msg') });
            this.showImg = false;
            continue;
          } else {
            const formData = new FormData();
            formData.append("files", file);

            const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
            const requestBody = {
              forTenantCode: this.configService.getForTenantCode(),
              countryCode: country[0].countryCode,
              uploadType: this.docType,
              originalFilename: file.name
            }
            this.blockedDocument = true;

            this.cs.generateUploadFileSignedUrl(requestBody).subscribe((data: any) => {
              const urlForFileUpload = data?.signedUrl;
              if (urlForFileUpload) {
                this.cs.uploadFile(file, urlForFileUpload, data.headers).subscribe(() => {
                  const reqBody = {
                    forTenantCode: this.configService.getForTenantCode(),
                    countryCode: country[0].countryCode,
                    uploadType: this.docType,
                    originalFilename: file.name,
                    fileName: data.fileName,
                    createdAt: data.createdAt,
                    signedToken: data.signedToken
                  }
                  this.cs.verifyUploadedFile(reqBody, this.docType).subscribe((verifyResult: any) => {
                    this.blockedDocument = false;
                    this.messageService.add({ key: "fileupload", severity: 'success', summary: 'Success', detail: this.cs.getLabel(this.showImg ? 'lbl_img_uploaded_successfully' : 'lbl_file_uploaded_successfully') });
                    const fileId = verifyResult.find(ele => ele.thumbnailSize === null)?.resourceId
                    if (this.isMultipleFilesUpload) {
                      if (verifyResult && verifyResult.length > 0) {
                        this.uploadedFileIds.push(fileId);
                        this.uploadedFiles.push({ file, fileId });
                      }
                    } else {
                      this.uploadedFileIds = [];
                      this.uploadedFiles = [];
                      this.uploadedFileIds.push(fileId);
                      this.uploadedFiles.push({ file, fileId });
                    }
                    this.fileUploadEvent.emit(this.uploadedFileIds);
                    this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode]?.setValue(this.uploadedFileIds);
                    this.formName.get(this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode)?.updateValueAndValidity();
                    this.cd.detectChanges();
                  }, () => this.blockedDocument = false)
                })
              }
            });
          }
        }
      }
    } else {
      if (this.field.inputCode === 'fileImgPdf') {
        this.messageService.add({ key: "fileupload", severity: 'error', summary: 'Error', detail: this.cs.getLabel('accepted_file_format_for_doc') });
      } else if (this.field.inputCode === 'fileImg') {
        this.messageService.add({ key: "fileupload", severity: 'error', summary: 'Error', detail: this.cs.getLabel('accepted_file_format_for_image') });
      }
    }
  }

  checkFileExtension(file) {
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return false;
    }
    const fileExtension = fileName.substring(lastDotIndex + 1).toLowerCase();
    const acceptedExtensions = this.accept.split(',').map(ext => ext.trim().toLowerCase().replace('*', ''));
    return acceptedExtensions.some(ext => fileExtension === ext || ext.includes(fileExtension));
  }

  getDocumentByFileId(fileId) {
    this.entityService.getFile(fileId, AppSettings.DOCUMENTS_TYPE.DOCUMENT, 'no').subscribe(result => {
      const file = new File([result], this.moduleName);
      this.uploadedFiles.push({ file, fileId });
      const reader = new FileReader();
      reader.readAsDataURL(result);
      reader.onloadend = () => {
        const base64data = reader.result;
        this.img = base64data;
      }
    })
  }

  downloadDocument(event) {
    if (event) {
      const fileId = event?.fileId;
      const documentTypes = event?.file?.type;
      const fileName = event?.file?.name;
      this.getFile(fileId, documentTypes, fileName);
    }
  }

  getFile(imgFileId, documentTypes, fileName) {
    this.entityService.getFile(imgFileId, AppSettings.DOCUMENTS_TYPE.DOCUMENT, 'no').subscribe(result => {
      const blob = new Blob([result], { type: `${documentTypes}` });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    });
  }

  get isDocumentModule(): boolean {
    return this.moduleName === 'protocol_site' || this.moduleName === 'protocol_product' || this.moduleName === 'protocol_purchase_order' || this.moduleName === 'protocol';
  }

}